// @flow

import { API } from "aws-amplify";
import React, { useState, useEffect, useContext } from "react";

import { listUsers } from "./../graphql/queries";
import { updateUser as updateUserMutation } from "./../graphql/mutations";
import UserContext from "./../utils/UserContext";
import NavigationBar from "./NavigationBar.react";

import ListGroup from "react-bootstrap/ListGroup";
import Button from "react-bootstrap/Button";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import { Container, Row, Col } from "react-bootstrap";
import { ReactComponent as PencilSquare } from "bootstrap-icons/icons/pencil-square.svg";

import "./AdminView.css";

type Props = $ReadOnly<{}>;

// 정렬에 사용할 키 타입
type SortKey =
  | "createdAt"
  | "username"
  | "realname"
  | "organization"
  | "broadcaster";

type SortConfig = {
  key: SortKey,
  direction: "asc" | "desc",
};

async function fetchAllUsers() {
  const apiData = await API.graphql({
    query: listUsers,
    variables: {},
  });
  return apiData.data?.listUsers?.items.reverse() ?? [];
}

export default function AdminView(props: Props): React$Element<any> {
  const [users, setUsers] = useState([]);
  const [toggled, setToggled] = useState(0);
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: "createdAt",
    direction: "desc",
  });
  const userContext = useContext(UserContext);

  useEffect(() => {
    async function fetch() {
      const users = await fetchAllUsers();
      setUsers(users);
    }
    fetch();
  }, [toggled]);

  // 공통 업데이트 함수: allowAllAccess, realName, organization 등 여러 필드를 업데이트할 때 사용
  async function updateUserCommon(
    user: {
      id: string,
      username: string,
      isSuperAdmin: boolean,
      allowAllAccess?: boolean,
      realName?: string,
      organization?: Array<string> | string,
      ...
    },
    extraInput: { [string]: any }
  ) {
    if (!checkPermission()) {
      return;
    }

    // organization이 String Set인 경우 GraphQL에서는 [String] 형태일 가능성이 높으므로 배열로 맞춰줌
    let organizationInput = user.organization;
    if (typeof organizationInput === "string") {
      organizationInput = [organizationInput];
    }

    const input = {
      id: user.id,
      username: user.username,
      // 기존 코드 유지: name은 username으로 세팅
      name: user.username,
      isSuperAdmin: user.isSuperAdmin,
      allowAllAccess: user.allowAllAccess ?? false,
      realName: user.realName,
      organization: organizationInput,
      ...extraInput,
    };

    await API.graphql({
      query: updateUserMutation,
      variables: { input },
    });

    setToggled((t) => t + 1);
  }

  async function updateUserAllAccess(
    user: {
      id: string,
      username: string,
      name: string,
      isSuperAdmin: boolean,
      ...
    },
    allowAccess
  ) {
    if (!checkPermission()) {
      return;
    }
    await API.graphql({
      query: updateUserMutation,
      variables: {
        input: {
          id: user.id,
          username: user.username,
          name: user.username,
          isSuperAdmin: user.isSuperAdmin,
          allowAllAccess: allowAccess,
        },
      },
    });
    setToggled(toggled + 1);
  }

  async function updateUserBroadcaster(
    user: {
      id: string,
      username: string,
      name: string,
      isSuperAdmin: boolean,
      allowAllAccess?: boolean,
      isBroadcaster?: boolean,
      ...
    },
    isBroadcaster: boolean
  ) {
    if (!checkPermission()) {
      return;
    }

    await API.graphql({
      query: updateUserMutation,
      variables: {
        input: {
          id: user.id,
          username: user.username,
          name: user.username,
          isSuperAdmin: user.isSuperAdmin,
          allowAllAccess: user.allowAllAccess ?? false,
          isBroadcaster,
        },
      },
    });

    setToggled((t) => t + 1);
  }

  // Name(=realName) 수정 핸들러
  const handleEditRealName = async (user) => {
    const nextName = window.prompt("새 이름을 입력하세요", user.realName || "");
    if (nextName == null) return; // 취소
    await updateUserCommon(user, { realName: nextName });
  };

  // Organization(String Set) 수정 핸들러
  const handleEditOrganization = async (user) => {
    const currentOrg = Array.isArray(user.organization)
      ? user.organization.join(", ")
      : user.organization || "";

    const nextOrg = window.prompt(
      '새 Organization을 입력하세요. \n(여러 개일 경우 콤마로 구분: "JYP, SM, YG")',
      currentOrg
    );
    if (nextOrg == null) return; // 취소

    const orgArray = nextOrg
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    await updateUserCommon(user, { organization: orgArray });
  };

  // Obviously change this to a real permission check later
  function checkPermission() {
    const myUser = users.filter(
      (user) => user.username === userContext.username
    );
    if (myUser.length === 0) {
      return false;
    }
    return myUser[0].isSuperAdmin;
  }

  // 정렬 버튼 클릭 핸들러
  const handleSort = (nextKey: SortKey) => {
    setSortConfig((prev) => {
      if (prev.key === nextKey) {
        // 같은 컬럼 다시 누르면 asc <-> desc 토글
        return {
          key: nextKey,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      // 다른 컬럼 클릭하면 기본 asc로 정렬
      return {
        key: nextKey,
        direction: "asc",
      };
    });
  };

  // 정렬 아이콘 렌더링
  const renderSortIcon = (columnKey: SortKey) => {
    if (sortConfig.key !== columnKey) return null;
    return sortConfig.direction === "asc" ? " ▲" : " ▼";
  };

  // 정렬된 유저 리스트 생성
  const sortedUsers = [...users].sort((a, b) => {
    const { key, direction } = sortConfig;

    let aVal;
    let bVal;

    if (key === "username") {
      aVal = (a.username || "").toLowerCase();
      bVal = (b.username || "").toLowerCase();
    } else if (key === "realname") {
      aVal = (a.realName || "").toLowerCase();
      bVal = (b.realName || "").toLowerCase();
    } else if (key === "organization") {
      aVal = (a.organization ? a.organization[0] : "").toLowerCase();
      bVal = (b.organization ? b.organization[0] : "").toLowerCase();
    } else if (key === "broadcaster") {
      aVal = a.isBroadcaster ? 1 : 0;
      bVal = b.isBroadcaster ? 1 : 0;
    } else if (key === "createdAt") {
      aVal = a.createdAt || "";
      bVal = b.createdAt || "";
    } else {
      // role 정렬: Admin -> allowAllAccess User -> 일반 User
      const aRank = a.isSuperAdmin ? 0 : a.allowAllAccess ? 1 : 2;
      const bRank = b.isSuperAdmin ? 0 : b.allowAllAccess ? 1 : 2;
      aVal = aRank;
      bVal = bRank;
    }

    if (aVal < bVal) return direction === "asc" ? -1 : 1;
    if (aVal > bVal) return direction === "asc" ? 1 : -1;
    return 0;
  });

  return (
    <>
      <NavigationBar />
      <Container fluid>
        <Row className="p-2 ms-1 mt-3">
          <h1>Admin View</h1>
          <Tabs defaultActiveKey="users">
            <Tab eventKey="users" title="Users">
              <Col xs={12} md={10} lg={10} className="pt-4">
                <ListGroup>
                  <ListGroup.Item variant="primary">
                    <Row className="fw-bold">
                      <Col xs={2} onClick={() => handleSort("username")}>
                        <span style={{ cursor: "pointer" }}>
                          Username{renderSortIcon("username")}
                        </span>
                      </Col>
                      <Col xs={2} onClick={() => handleSort("realname")}>
                        <span style={{ cursor: "pointer" }}>
                          Name{renderSortIcon("realname")}
                        </span>
                      </Col>
                      <Col xs={2} onClick={() => handleSort("organization")}>
                        <span style={{ cursor: "pointer" }}>
                          Organization{renderSortIcon("organization")}
                        </span>
                      </Col>
                      <Col xs={2} onClick={() => handleSort("broadcaster")}>
                        <span style={{ cursor: "pointer" }}>
                          Broadcaster{renderSortIcon("broadcaster")}
                        </span>
                      </Col>
                      <Col xs={2} onClick={() => handleSort("createdAt")}>
                        <span style={{ cursor: "pointer" }}>
                          Created At{renderSortIcon("createdAt")}
                        </span>
                      </Col>
                      <Col xs={2} onClick={() => handleSort("role")}>
                        <span style={{ cursor: "pointer" }}>
                          Action{renderSortIcon("role")}
                        </span>
                      </Col>
                    </Row>
                  </ListGroup.Item>
                  {sortedUsers.map((user) => {
                    const hasRealName = !!user.realName;
                    const orgText = Array.isArray(user.organization)
                      ? user.organization.join(", ")
                      : user.organization || "";
                    const hasOrg = !!orgText;

                    return (
                      <ListGroup.Item key={user.username} action>
                        <Row className="align-items-center">
                          <Col xs={2} style={{ fontWeight: 500 }}>
                            {user.username}
                          </Col>
                          <Col
                            xs={2}
                            className={`d-flex align-items-center clickable-cell`}
                            onClick={() => {
                              if (!hasRealName) handleEditRealName(user);
                            }}
                          >
                            <span>
                              {hasRealName ? user.realName : "\u00A0"}
                            </span>

                            {hasRealName && (
                              <button
                                className="p-0 ms-2 border-0 bg-transparent text-muted icon-button"
                                onClick={(e) => {
                                  e.stopPropagation(); // 셀 클릭 무효화
                                  handleEditRealName(user);
                                }}
                              >
                                <PencilSquare width={15} height={15} />
                              </button>
                            )}
                          </Col>

                          <Col
                            xs={2}
                            className={`d-flex align-items-center clickable-cell`}
                            onClick={() => {
                              if (!hasOrg) handleEditOrganization(user);
                            }}
                          >
                            <span>{hasOrg ? orgText : "\u00A0"}</span>

                            {hasOrg && (
                              <button
                                className="p-0 ms-2 border-0 bg-transparent text-muted icon-button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditOrganization(user);
                                }}
                              >
                                <PencilSquare width={15} height={15} />
                              </button>
                            )}
                          </Col>
                          <Col
                            xs={2}
                            className="clickable-cell"
                            onClick={() => {
                              const current = !!user.isBroadcaster; // null/undefined 대비
                              const next = !current;
                              const label = next
                                ? "broadcaster 권한을 부여"
                                : "broadcaster 권한을 제거";

                              if (
                                window.confirm(`정말 ${label}하시겠습니까?`)
                              ) {
                                updateUserBroadcaster(user, next);
                              }
                            }}
                          >
                            <span>{user.isBroadcaster ? "Yes" : "\u00A0"}</span>
                          </Col>
                          <Col xs={2}>{user.createdAt.split("T")[0]}</Col>
                          <Col xs={2}>
                            {user.isSuperAdmin ? (
                              "Admin"
                            ) : user.allowAllAccess ? (
                              <Button
                                size="sm"
                                variant="outline-danger"
                                onClick={() => {
                                  updateUserAllAccess(user, false);
                                }}
                              >
                                Revoke Access
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline-primary"
                                onClick={() => {
                                  updateUserAllAccess(user, true);
                                }}
                              >
                                Grant Access
                              </Button>
                            )}
                          </Col>
                        </Row>
                      </ListGroup.Item>
                    );
                  })}
                </ListGroup>
              </Col>
            </Tab>
            <Tab></Tab>
          </Tabs>
        </Row>
      </Container>
    </>
  );
}
